import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { decodeAPITokenJWT } from 'fym-common/src/utils/decodeAPITokenJWT';
import { Music24JWTUser } from 'fym-common/src/db/userTypes';
import { Music24QuotaKind } from 'fym-shared/src/music24/music24Plans';
import type { UpsellSurface } from 'fym-shared/src/music24/watermarkUpsell';
import { SMLUpgradeFeature } from '../../../modules/music24/config/music24PlanCapabilities';
import { appInfoSliceName } from './constants';
import { AppInfoState } from './state';
import { isWorkspaceChange } from './isWorkspaceChange';

type SetUserTokenPayload = { token: string | null };
type SetAccountIdPayload = { accountId: number | null };
type SetIsOnlinePayload = { isOnline: boolean };
type SetIsServerUpPayload = { isServerUp: boolean };
type ToggleSMLPricingModalPayload = { shouldShow: boolean };
type SetTourActivePayload = { active: boolean };
type OpenSMLUpgradePromptPayload = {
  feature: SMLUpgradeFeature;
  limit?: number | null;
  used?: number | null;
  kind?: Music24QuotaKind | null;
  resetsAt?: string | null;
  subject?: string | null;
  upsellSurface?: UpsellSurface | null;
};

const appInfoSlice = createSlice({
  name: appInfoSliceName,
  initialState: AppInfoState,
  reducers: {
    setUserToken: (state, action: PayloadAction<SetUserTokenPayload>) => {
      const previousLicenseId = state.userTokenDecoded?.licenseId;
      state.userToken = action.payload.token;
      state.userTokenDecoded = decodeAPITokenJWT<Music24JWTUser>(action.payload.token);
      // The active artist belongs to the workspace of the old token. Drop it in the same update,
      // so that no render has the new workspace with the old artist. `WorkspaceChangeReset` then
      // resets the caches.
      if (isWorkspaceChange(previousLicenseId, state.userTokenDecoded?.licenseId)) {
        state.accountId = null;
      }
      // A sign-out ends the tour.
      if (action.payload.token === null) {
        state.tourActive = false;
      }
    },
    setAccountId: (state, action: PayloadAction<SetAccountIdPayload>) => {
      state.accountId = action.payload.accountId;
    },
    setIsOnline: (state, action: PayloadAction<SetIsOnlinePayload>) => {
      state.isOnline = action.payload.isOnline;
    },
    setIsServerUp: (state, action: PayloadAction<SetIsServerUpPayload>) => {
      state.isServerUp = action.payload.isServerUp;
    },
    toggleSMLPricingModal: (state, action: PayloadAction<ToggleSMLPricingModalPayload>) => {
      state.showSMLPricingModal = action.payload.shouldShow;
    },
    openSMLUpgradePrompt: (state, action: PayloadAction<OpenSMLUpgradePromptPayload>) => {
      state.smlUpgradePrompt = {
        feature: action.payload.feature,
        limit: action.payload.limit ?? null,
        used: action.payload.used ?? null,
        kind: action.payload.kind ?? null,
        resetsAt: action.payload.resetsAt ?? null,
        subject: action.payload.subject ?? null,
        upsellSurface: action.payload.upsellSurface ?? null,
      };
    },
    closeSMLUpgradePrompt: (state) => {
      state.smlUpgradePrompt = null;
    },
    setTourActive: (state, action: PayloadAction<SetTourActivePayload>) => {
      state.tourActive = action.payload.active;
    },
  },
});

const { actions, reducer } = appInfoSlice;
export const {
  setUserToken,
  toggleSMLPricingModal,
  setAccountId,
  setIsOnline,
  setIsServerUp,
  openSMLUpgradePrompt,
  closeSMLUpgradePrompt,
  setTourActive,
} = actions;
export { reducer as appInfoReducer };
